import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSetAtom } from 'jotai';
import PropTypes from 'prop-types';

import {
  Grow,
  Popover,
  Typography,
  IconButton,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  FormControl,
  Select,
  Box,
  MenuItem,
  ListItem,
  List,
  Divider,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { openSnackbarAdviseAtom } from '../../../Atoms';
import StyledSwitch from '../../../Components/Inputs/StyledSwitch';

const ContactsManagerWindow = function ContactsManagerWindow({
  open,
  close,
  onChangeSelection,
  addNewContact,
  listTitle,
  handleChangeContactStatus,
  valueTypesArray,
  errorMessage,
  sourceArray = [],
  title = null,
  validationError = false,
  isPhone = false,
  anchorEl = null,
  selectedItem = '',
}) {
  const intl = useIntl();
  const [valueType, setValueType] = useState('');
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const [inputValue, setInputValue] = useState(isPhone ? '+39' : '');
  const [showDisabled, setShowDisabled] = useState(false);
  const [filteredArray, setFilteredArray] = useState([]);

  useEffect(() => {
    setFilteredArray(
      showDisabled ? sourceArray : sourceArray.filter(({ active }) => active)
    );
  }, [sourceArray, showDisabled]);

  const handleClick = (event, value) => {
    event.stopPropagation();
    if (selectedItem !== value) onChangeSelection(value);
  };

  const handleShowDisabled = (event) => {
    event.stopPropagation();
    setShowDisabled((prev) => !prev);
  };

  const handleChange = (value, typeId, active, isPriority, isMobile) => {
    const isAlreadyAPrincipal = sourceArray?.some(
      (item) => item.typeId === typeId && item.active && !active
    );
    if (isAlreadyAPrincipal && isPriority) {
      setOpenSnackbarAdvise({
        message: isPhone
          ? intl.messages.ALREADY_PRINCIPAL_PHONENUMBER_ACTIVE
          : intl.messages.ALREADY_PRINCIPAL_EMAIL_ACTIVE,
        severity: 'error',
      });
    } else {
      handleChangeContactStatus(value, isPriority, isMobile);
    }
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={() => {
        close();
        setValueType('');
        setFilteredArray([]);
        setInputValue(isPhone ? '+39' : '');
        setShowDisabled(false);
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      TransitionComponent={Grow}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '5px 15px 10px 10px',
          width: '500px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              p: '20px',
            }}
          >
            {listTitle || ''}
          </Typography>
          <StyledSwitch
            label={intl.messages.SHOW_DISABLED}
            labelPlacement="start"
            onChange={handleShowDisabled}
            checked={showDisabled}
            sx={{
              gap: 2,
              marginLeft: '100px',
            }}
          />
        </Box>
        <Divider />
        <List sx={{ height: '400px', overflow: 'auto', p: '5px' }}>
          {filteredArray?.map(
            ({ value, type, typeId, active, isPriority, isMobile }) => (
              <ListItem
                key={value}
                disablePadding
                secondaryAction={
                  <Tooltip
                    title={
                      active
                        ? intl.messages.DEACTIVATE
                        : intl.messages.DEACTIVATED_NUMBER
                    }
                    placement="bottom"
                  >
                    <Box onClick={(event) => event.stopPropagation()}>
                      <StyledSwitch
                        label={
                          active ? intl.messages.ACTIVE : intl.messages.INACTIVE
                        }
                        labelPlacement="start"
                        checked={active}
                        onChange={() =>
                          handleChange(
                            value,
                            typeId,
                            active,
                            isPriority,
                            isMobile
                          )
                        }
                        sx={{ gap: 2 }}
                      />
                    </Box>
                  </Tooltip>
                }
              >
                <ListItemButton
                  sx={{
                    borderRadius: '18px',
                    border: (theme) =>
                      selectedItem === value
                        ? `2px solid ${theme.palette.primary.main}`
                        : 'unset',
                    opacity: active ? 1 : 0.5,
                    pointerEvents: active ? 'auto' : 'none',
                  }}
                  selected={selectedItem === value}
                  onClick={(event) => handleClick(event, value)}
                >
                  <Tooltip title={intl.messages.SELECT} placement="top-start">
                    <Box sx={{ width: '100%' }}>
                      <ListItemText primary={value} secondary={type || ''} />
                    </Box>
                  </Tooltip>
                </ListItemButton>
              </ListItem>
            )
          )}
        </List>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
          }}
        >
          <TextField
            error={validationError}
            fullWidth
            placeholder={intl.messages.ADD_NEW}
            onClick={(event) => event.stopPropagation()}
            variant="outlined"
            size="small"
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
          />
          <FormControl fullWidth size="small">
            <Select
              fullWidth
              required
              value={valueType}
              onClick={(event) => event.stopPropagation()}
              onChange={(e) => setValueType(e.target.value)}
            >
              {valueTypesArray?.map(
                ({ label: optLabel, value, isPriority }) => {
                  const isDisabled =
                    isPriority &&
                    sourceArray?.some(
                      (item) => item.typeId === value && item.active
                    );
                  return (
                    <MenuItem key={value} value={value} disabled={isDisabled}>
                      {optLabel}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </FormControl>
          <Tooltip
            title={`${intl.messages.ADD_NEW} ${title}`}
            placement="bottom"
          >
            <IconButton
              onClick={() => {
                if (validationError) {
                  setOpenSnackbarAdvise({
                    message: errorMessage,
                    severity: 'error',
                  });
                } else {
                  const selectedValueType = valueTypesArray.find(
                    ({ value }) => value === valueType
                  );
                  const adjustedValue = inputValue.replace(/\s+/g, '');
                  addNewContact(
                    adjustedValue,
                    {
                      value: selectedValueType?.value,
                      label: selectedValueType?.label,
                    },
                    !sourceArray?.length ? 1 : 0,
                    selectedValueType.isPriority,
                    selectedValueType.isMobile
                  );
                  setInputValue(isPhone ? '+39' : '');
                  setValueType('');
                  setFilteredArray([]);
                }
              }}
              disabled={!valueType || !inputValue?.length}
            >
              <AddCircleOutlineOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Popover>
  );
};

ContactsManagerWindow.propTypes = {
  anchorEl: PropTypes.object,
  open: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  sourceArray: PropTypes.array,
  onChangeSelection: PropTypes.func.isRequired,
  addNewContact: PropTypes.func.isRequired,
  selectedItem: PropTypes.string,
  listTitle: PropTypes.string,
  title: PropTypes.string,
  validationError: PropTypes.bool,
  handleChangeContactStatus: PropTypes.func,
  isPhone: PropTypes.bool,
  valueTypesArray: PropTypes.array,
  errorMessage: PropTypes.string,
};

export default ContactsManagerWindow;
